"use client";

/**
 * MUI components wired to Next.js <Link>, usable from Server Components
 * (a Server Component can't pass the Link function itself as a prop).
 */
import Segmented from "@/components/premium/Segmented";
import Link from "@/components/AppLink";
import { Box, Button, CardActionArea, Chip, Link as MuiLink, Pagination, PaginationItem, Stack, type BoxProps, type ButtonProps, type CardActionAreaProps, type ChipProps, type LinkProps } from "@mui/material";

/**
 * Files and other API addresses (downloads) are plain links: a Next.js link would fetch them ahead of time in the
 * background on every page that shows one (each download signs a storage address and redirects to it, which the
 * browser then blocks), slowing the page and the server for nothing.
 */
const isFile = (href: string) => href.startsWith("/api/");

export function ButtonLink({ href, ...props }: ButtonProps & { href: string }) {
  return isFile(href) ? <Button component="a" href={href} {...props} /> : <Button component={Link} href={href} {...props} />;
}

/** A plain box that is a link (pills, "See all" links styled with sx). */
export function BoxLink({ href, ...props }: Omit<BoxProps<"a">, "component" | "href"> & { href: string }) {
  return isFile(href) ? <Box component="a" href={href} {...props} /> : <Box component={Link} href={href} {...props} />;
}

export function TextLink({ href, ...props }: LinkProps & { href: string }) {
  return isFile(href) ? <MuiLink href={href} {...props} /> : <MuiLink component={Link} href={href} {...props} />;
}

export function CardActionLink({ href, ...props }: CardActionAreaProps & { href: string }) {
  return <CardActionArea component={Link} href={href} {...props} />;
}

export function LinkTabs({
  value,
  tabs,
  ariaLabel,
}: {
  value: string;
  tabs: { value: string; label: string; href: string }[];
  ariaLabel: string;
  /** Many tabs on a narrow screen: scroll sideways instead of squeezing. */
  scrollable?: boolean;
}) {
  return (
    // Board 0: one pill tab bar everywhere (it scrolls sideways on a narrow screen).
    <Segmented label={ariaLabel} value={value} items={tabs} />
  );
}

/** A chip that opens a page (filters like "Uploads 12"). */
export function ChipLink({ href, ...props }: ChipProps & { href: string }) {
  return <Chip component={Link} href={href} clickable {...props} />;
}

/** Page numbers as links. `template` is the address with __PAGE__ where the number goes. */
export function LinkPagination({ page, count, template }: { page: number; count: number; template: string }) {
  if (count <= 1) return null;
  return (
    <Stack sx={{ alignItems: "center", mt: 2.5 }}>
      <Pagination page={Math.min(page, count)} count={count} renderItem={(item) => <PaginationItem component={Link} href={template.replace("__PAGE__", String(item.page ?? 1))} {...item} />} />
    </Stack>
  );
}
